/** Onboarding amount input: focus/blur display + dollar storage helpers. */

export type AmountEditUnit = "k" | "dollar";

/** Strip everything except digits (focus drafts are bare integers). */
export const stripToDigits = (raw: string): string => raw.replace(/\D/g, "");

export const clampAmount = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Dollars → integer k for focus display (85000 → "85", 1500000 → "1500"). */
export const dollarsToK = (dollars: number): number =>
  Math.round(Math.max(0, dollars) / 1_000);

export const kToDollars = (k: number): number => Math.max(0, k) * 1_000;

/**
 * Blur / slider idle compact form for k-scale amount steps.
 * Million tier keeps one decimal (`1.0M`, `1.5M`).
 */
export function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${Math.round(value / 1_000)}k`;
  }
  return String(value);
}

/** Expenses blur: en-US thousands separators. */
export function formatGroupedDollars(value: number): string {
  return value.toLocaleString("en-US");
}

/** Focus draft string for a stored dollar amount (no grouping). */
export function toFocusDigits(
  dollars: number,
  editUnit: AmountEditUnit,
): string {
  if (editUnit === "k") {
    return String(dollarsToK(dollars));
  }
  return String(Math.round(Math.max(0, dollars)));
}

/**
 * 手输框的 clamp 上界（fe-spec §1.4.2-A1）：与滑杆量程 `max` 解耦。
 * `inputMax` 缺省时退化为 `max` —— 与改造前逐值一致。
 * 刻意不支持「无上界」：手滑多按几位会打爆后端公式。
 */
export const resolveAmountInputClampMax = (numeric: {
  max: number;
  inputMax?: number;
}): number => numeric.inputMax ?? numeric.max;

/**
 * Parse a focus draft into clamped dollars.
 * Empty draft → 0. Non-digits are ignored.
 *
 * `clampMax` 是**手输上界**（`inputMax ?? max`，见 §1.4.2-A1），不是滑杆量程。
 * 调用方传 `resolveAmountInputClampMax(numeric)`。
 */
export function parseAmountDraft(
  draft: string,
  editUnit: AmountEditUnit,
  min: number,
  clampMax: number,
): number {
  const digits = stripToDigits(draft);
  if (digits === "") {
    return clampAmount(0, min, clampMax);
  }
  const n = Number.parseInt(digits, 10);
  if (!Number.isFinite(n)) {
    return clampAmount(0, min, clampMax);
  }
  const dollars = editUnit === "k" ? kToDollars(n) : n;
  return clampAmount(dollars, min, clampMax);
}

/**
 * Apply a raw input string: returns next draft (digits, clamped if over
 * `clampMax`) and dollar value to store.
 *
 * `clampMax` 同 {@link parseAmountDraft} —— 手输上界，不是滑杆 `max`。
 * 值落在 `clampMax` 以内时 draft **不被回写**（AC21：`1`→`10`→`100`→`1000`
 * 第 4 位不被吞）；只有真的越过手输上界才回写截断后的数字。
 */
export function applyAmountInput(
  raw: string,
  editUnit: AmountEditUnit,
  min: number,
  clampMax: number,
): { draft: string; dollars: number } {
  const digits = stripToDigits(raw);
  if (digits === "") {
    return { draft: "", dollars: clampAmount(0, min, clampMax) };
  }
  const dollars = parseAmountDraft(digits, editUnit, min, clampMax);
  // Reflect clamp in draft (e.g. typed k above inputMax → inputMax k digits)
  return { draft: toFocusDigits(dollars, editUnit), dollars };
}

/**
 * Editable amount row: digits in the input; scale letters live in the suffix
 * (Figma: `$ 85 | k / year`, `$ 1.0 | M total`). Focus always edits in k for
 * `editUnit: "k"`.
 */
export function resolveEditableAmountView(options: {
  value: number;
  focused: boolean;
  draft: string;
  editUnit: AmountEditUnit;
  baseSuffix: string;
}): { text: string; suffix: string } {
  const { value, focused, draft, editUnit, baseSuffix } = options;
  if (editUnit === "dollar") {
    return {
      text: focused ? draft : formatGroupedDollars(value),
      suffix: baseSuffix,
    };
  }
  const kSuffix = baseSuffix.length > 0 ? `k ${baseSuffix}` : "k";
  const mSuffix = baseSuffix.length > 0 ? `M ${baseSuffix}` : "M";
  if (focused) {
    return { text: draft, suffix: kSuffix };
  }
  if (value >= 1_000_000) {
    return {
      text: (value / 1_000_000).toFixed(1),
      suffix: mSuffix,
    };
  }
  return { text: String(dollarsToK(value)), suffix: kSuffix };
}
